import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../networking/client';
import { RootState } from '../store';
import { toast } from 'react-toastify';
import { fetchSalesOrders } from './salesOrders';

export interface ConsultationRecommendation {
  consultation_recommendations_id: number;
  consultation_recommendations_name: string;
  consultation_recommendations_description: string;
  service_id: number;
}

export interface ConsultationRecommendationsState {
  status: string;
  editingStatus: string;
  consultationRecommendations: ConsultationRecommendation[];
}

const initialState: ConsultationRecommendationsState = {
  status: 'idle',
  editingStatus: 'idle',
  consultationRecommendations: [],
};

export const fetchConsultationRecommendations = createAsyncThunk(
  'consultationRecommendations/fetchAll',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const consultationRecommendationsResponse: any = await client.get(
        '/healthpoint/consultation-recommendations'
      );
      return consultationRecommendationsResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

type EditConsultationRecommendation = {
  consultationsRecommendationDescription: string;
  consultationRecommendationId: number;
};

export const editConsultationRecommendation = createAsyncThunk(
  'consultationRecommendation/edit',
  async (
    editConsultationRecommendation: EditConsultationRecommendation,
    { dispatch, rejectWithValue }
  ) => {
    try {
      await client.put(
        `/healthpoint/consultation-recommendation/${editConsultationRecommendation.consultationRecommendationId}`,
        {
          consultationsRecommendationDescription:
            editConsultationRecommendation.consultationsRecommendationDescription,
        }
      );
      dispatch(fetchConsultationRecommendations());
      dispatch(fetchSalesOrders());
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const consultationRecommendationsSlice = createSlice({
  name: 'consultationRecommendations',
  initialState,
  reducers: {
    resetConsultationRecommendations: (_) => {
      return initialState;
    },
    resetDeletingStatus: (state) => {
      return {
        ...state,
        deletingStatus: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConsultationRecommendations.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchConsultationRecommendations.fulfilled, (state, action) => {
        state.consultationRecommendations = action.payload.data.consultationRecommendations;
        state.status = 'fulfilled';
      })
      .addCase(fetchConsultationRecommendations.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })
      .addCase(editConsultationRecommendation.pending, (state) => {
        state.editingStatus = 'pending';
      })
      .addCase(editConsultationRecommendation.fulfilled, (state) => {
        state.editingStatus = 'fulfilled';
        toast.success('Successfully updated the recommendations.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      })
      .addCase(editConsultationRecommendation.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.editingStatus = 'rejected';
      });
  },
});

export const { resetConsultationRecommendations, resetDeletingStatus } =
  consultationRecommendationsSlice.actions;

export default consultationRecommendationsSlice.reducer;

export const selectConsultationRecommendations = (state: RootState) =>
  state.consultationRecommendations.consultationRecommendations;
export const selectConsultationRecommendationStatus = (state: RootState) =>
  state.consultationRecommendations.status;
export const selectConsultationRecommendationEditingStatus = (state: RootState) =>
  state.consultationRecommendations.editingStatus;
